/**
 * The selected agent's identity, in service context (task 796), and on the
 * Advanced → Agents detail page (task 1309).
 *
 * Name, description, whether it is active, when it last changed, and which
 * node(s) of THIS flow it occupies. In service context the header carries the
 * one link out ("Open in Advanced") and nothing editable.
 *
 * The flow props are optional (task 1309): an agent on its own page has no flow,
 * so without `nodes` there are no node chips and no "Open in Advanced" link.
 * `showMetadata` swaps the right-hand column for the Agents page's: an actions
 * slot, who last updated and who created the agent (names via `UserName`, the
 * only way an audit id reaches the screen), and the labelled Agent id with a
 * copy affordance. `onOpenHistory` adds the version chip and a "History →" link
 * that open the page's History tab rather than the slide-over drawer.
 *
 * Editing (task 1310): with `editing`, the name, description and Active status
 * become inputs in place and every change goes to `onFieldChange`; the page
 * passes the edited agent back in as `agent`, so the inputs show the draft.
 */
import { Link } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon, ClipboardIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import type { Agent } from '@/types';
import type { AgentNode } from '@/types/definition';
import { UserName } from '@/components/ui/UserName';
import { VersionStatusChip } from '@/components/versioning/VersionStatusChip';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useResourceVersionStatus } from '@/hooks/useResourceVersions';
import { useOptionalToast } from '@/contexts/ToastContext';

interface AgentIdentityHeaderProps {
  agent: Agent;
  /**
   * Every node in this flow that runs the agent, in declaration order. Absent
   * outside a flow (the Agents page), which hides the node chips and the
   * "Open in Advanced" link.
   */
  nodes?: AgentNode[];
  /**
   * Render the Agents page's right-hand column: `actions`, Updated / Created by
   * name, and the labelled Agent id. The service tab does not pass it.
   */
  showMetadata?: boolean;
  /** The header's actions (Edit, or Save changes · Cancel). Rendered only with `showMetadata`. */
  actions?: ReactNode;
  /**
   * Opens the page's own History view. When given, the header shows the version
   * chip and a "History →" link, both calling this instead of opening the
   * version history drawer.
   */
  onOpenHistory?: () => void;
  /** Render name, description and Active as inputs (task 1310). */
  editing?: boolean;
  /** Called on each input change; the page accumulates these into its changed fields. */
  onFieldChange?: (field: string, value: unknown) => void;
}

const inputClasses =
  'block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100';

const formatUpdated = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

/**
 * The version chip and its History link, on a page that has its own History
 * view. Split out so the version-status read happens only where the chip can
 * render; the dormant-account rule is the chip's own (nothing renders).
 */
function HeaderVersionChip({ agentId, onOpenHistory }: { agentId: string; onOpenHistory: () => void }) {
  const featureEnabled = useFlag('innovate.fe.resource-versioning');
  const { status } = useResourceVersionStatus('agent', agentId);

  if (!featureEnabled || !status.versioningEnabled) return null;

  return (
    <>
      <VersionStatusChip status={status} onOpenHistory={onOpenHistory} />
      <button
        type="button"
        onClick={onOpenHistory}
        data-testid="agent-header-history-link"
        className="text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
      >
        History →
      </button>
    </>
  );
}

/** Updated / Created by name, and the labelled Agent id with copy (task 1309). */
function AgentMetadataColumn({ agent, actions }: { agent: Agent; actions?: ReactNode }) {
  const toast = useOptionalToast();
  const updatedBy = agent.updatedBy ?? agent.createdBy;

  const copyId = async () => {
    try {
      await navigator.clipboard?.writeText(agent.id);
      toast?.info('Agent id copied', agent.id);
    } catch {
      toast?.error('Could not copy', 'Copy the Agent id manually.');
    }
  };

  return (
    <div
      data-testid="agent-identity-metadata"
      className="flex shrink-0 flex-col items-start gap-2 text-xs text-gray-500 dark:text-gray-400 md:items-end md:text-right"
    >
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      {/* An agent not yet saved (the create page, task 1312) has no id, dates or authors. */}
      {!agent.id ? (
        <p data-testid="agent-identity-unsaved" className="max-w-xs">
          Agent id, created and updated appear after the first save.
        </p>
      ) : (
        <>
          <div className="space-y-0.5">
            <p data-testid="agent-identity-updated">
              Updated {formatUpdated(agent.updatedAt)}
              {updatedBy && (
                <>
                  {' '}by <UserName userId={updatedBy} className="text-gray-700 dark:text-gray-200" />
                </>
              )}
            </p>
            <p data-testid="agent-identity-created">
              Created {formatUpdated(agent.createdAt)}
              {agent.createdBy && (
                <>
                  {' '}by <UserName userId={agent.createdBy} className="text-gray-700 dark:text-gray-200" />
                </>
              )}
            </p>
          </div>
          <div className="max-w-xs">
            <p className="inline-flex flex-wrap items-center gap-1">
              <span className="font-semibold text-gray-600 dark:text-gray-300">Agent id</span>
              <span className="break-all font-mono text-gray-700 dark:text-gray-200" data-testid="agent-identity-id">
                {agent.id}
              </span>
              <button
                type="button"
                onClick={copyId}
                data-testid="agent-identity-copy-id"
                aria-label="Copy Agent id"
                title="Copy Agent id"
                className="rounded p-0.5 text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:hover:text-gray-200"
              >
                <ClipboardIcon className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </p>
            <p className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">
              The id a flow definition or an API call uses to name this agent.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export function AgentIdentityHeader({
  agent,
  nodes,
  showMetadata = false,
  actions,
  onOpenHistory,
  editing = false,
  onFieldChange,
}: AgentIdentityHeaderProps) {
  // "Open in Advanced" is the service tab's link out; on the Agents page there
  // is no flow and nowhere further to go.
  const inFlow = nodes !== undefined;

  return (
    <section
      data-testid="agent-identity-header"
      className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className={editing ? 'min-w-0 flex-1' : 'min-w-0'}>
          {editing ? (
            <div className="max-w-2xl space-y-3">
              <div>
                <label htmlFor="agent-edit-name" className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                  Name
                </label>
                <input
                  id="agent-edit-name"
                  data-testid="agent-edit-name"
                  type="text"
                  value={agent.name}
                  onChange={(event) => onFieldChange?.('name', event.target.value)}
                  className={`mt-1 ${inputClasses}`}
                />
              </div>
              <div>
                <label
                  htmlFor="agent-edit-description"
                  className="block text-xs font-medium text-gray-500 dark:text-gray-400"
                >
                  Description
                </label>
                <textarea
                  id="agent-edit-description"
                  data-testid="agent-edit-description"
                  rows={3}
                  value={agent.description ?? ''}
                  onChange={(event) => onFieldChange?.('description', event.target.value)}
                  className={`mt-1 ${inputClasses}`}
                />
              </div>
            </div>
          ) : (
            <>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{agent.name}</h2>
              {agent.description && (
                <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-300">
                  {agent.description}
                </p>
              )}
            </>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {editing ? (
              <div className="inline-flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                <span className="font-medium text-gray-500 dark:text-gray-400">Status</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={agent.isActive}
                  aria-label="Active"
                  data-testid="agent-edit-active"
                  onClick={() => onFieldChange?.('isActive', !agent.isActive)}
                  className={`relative inline-flex h-5 w-9 flex-none items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                    agent.isActive ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      agent.isActive ? 'translate-x-4' : 'translate-x-0.5'
                    }`}
                  />
                </button>
                {agent.isActive ? 'Active — services may run this agent' : 'Inactive'}
              </div>
            ) : (
              <span
                data-testid="agent-active-badge"
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                  agent.isActive
                    ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-700 dark:bg-green-900/30 dark:text-green-200'
                    : 'border-gray-300 bg-gray-50 text-gray-600 dark:border-gray-600 dark:bg-gray-700/50 dark:text-gray-300'
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`h-1.5 w-1.5 rounded-full ${
                    agent.isActive ? 'bg-green-500' : 'bg-gray-400'
                  }`}
                />
                {agent.isActive ? 'Active' : 'Inactive'}
              </span>
            )}

            {onOpenHistory && !editing && <HeaderVersionChip agentId={agent.id} onOpenHistory={onOpenHistory} />}

            {/* One chip per node appearance — an agent used twice in a flow is
                two distinct runtime positions, each with its own settings. */}
            {(nodes ?? []).map((node) => (
              <span
                key={node.id}
                data-testid="agent-node-chip"
                className="inline-flex items-center rounded-full border border-indigo-300 bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700 dark:border-indigo-500 dark:bg-indigo-900/30 dark:text-indigo-200"
              >
                Node: <span className="ml-1 font-mono">{node.id}</span>
              </span>
            ))}
          </div>
        </div>

        {showMetadata ? (
          <AgentMetadataColumn agent={agent} actions={actions} />
        ) : (
          <div className="shrink-0 text-right text-xs text-gray-500 dark:text-gray-400">
            <p>Updated {formatUpdated(agent.updatedAt)}</p>
            {inFlow && (
              <Link
                to={`/agents/${agent.id}`}
                data-testid="agent-open-in-advanced"
                className="mt-2 inline-flex items-center gap-1 font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
              >
                Open in Advanced
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default AgentIdentityHeader;
